import { RedTeamTaskIcon } from '@/components/RedTeamTaskIcon'
import { AdditionalMarker, ScoredChip } from '@/components/JobFailure'
import { useState } from 'react'
import { Icon } from '@/components/Icon'
import { ModelLabel } from '@/components/ModelLabel'
import { PREPARING_JOB, StepMeter } from '@/components/RunFleet'
import { describeFailure, attemptFailureCode, attemptFailureMessage, scoredNotice } from '@/lib/failures'
import { failedAttempts, gradingIdentity, jobStage, type JobStage, type Rollout, type TaskInfo } from '@/lib/runs'

const STAGES: { stage: JobStage; label: string }[] = [
  { stage: 'running', label: 'Running' }, { stage: 'queued', label: 'Queued' },
  { stage: 'completed', label: 'Completed' }, { stage: 'stopped', label: 'Stopped by agent' },
  { stage: 'failed', label: 'Failed' },
]
const PRIORITY: Record<JobStage, number> = { running: 0, failed: 1, queued: 2, completed: 3, stopped: 4 }
const WAIT_LABELS: Record<string, string> = {
  preparing: 'Preparing', nodes_not_launched: 'Waiting for nodes', fleet_booting: 'Nodes starting',
  fleet_preparing: 'Staging image', fleet_unreachable: 'Node unreachable', fleet_stopped: 'Fleet stopped',
  slots_busy: 'Waiting for a slot', claimable: 'Next up', retry_backoff: 'Waiting to retry',
  retry_budget: 'Retry budget', model_api_paused: 'Model API paused', redelivery: 'Redelivery',
  not_retried: 'Not retried', stopping: 'Stopping',
}

function JobProgress({ job }: { job: Rollout }) {
  const stage = jobStage(job)
  if (stage === 'queued') return <span title={job.waiting?.text}>{WAIT_LABELS[job.waiting?.code ?? ''] ?? 'Waiting'}</span>
  if (stage === 'running') return <>
    {job.phase === 'preparing' ? <span title={PREPARING_JOB}>Preparing task</span> : gradingIdentity(job.attempt_kind) ? <span>Grading</span> : <StepMeter step={job.step} maxSteps={job.max_steps} />}
    {job.heartbeat?.elapsed_seconds != null && <small>{Math.round(job.heartbeat.elapsed_seconds / 60)}m elapsed</small>}
  </>
  const failure = failedAttempts(job.attempts).at(-1)
  const scored = job.attempts.find(attempt => attempt.classification === 'success')
  const result = scored?.summary
  const notice = scoredNotice(scored)
  // The authoring agent ended it on purpose: finished, graded by nobody, failed by nothing.
  if (stage === 'stopped') return <span title="The authoring agent ended this rollout after reading its telemetry.">Stopped by the authoring agent</span>
  if (stage === 'failed') return <span className="job-row-message" title={attemptFailureCode(failure)}><span>{attemptFailureMessage(failure) || describeFailure(attemptFailureCode(failure))?.label || 'Failed attempt'}</span><AdditionalMarker attempt={failure} /></span>
  return <>
    <span>{gradingIdentity(job.attempt_kind) ? 'Graded' : result?.steps != null ? `${result.steps} steps` : 'Finished'}{result?.duration_seconds != null && ` · ${Math.round(result.duration_seconds / 60)}m`}</span>
    {notice ? <span className="job-row-message"><ScoredChip attempt={scored} /><small>{notice.message || notice.code}</small><AdditionalMarker attempt={scored} /></span>
      : <AdditionalMarker attempt={scored} />}
  </>
}

/** A single scan-friendly list; filters narrow it without changing job selection. */
export function ExperimentJobs({ jobs, tasks, selectedJobId, onSelect }: {
  jobs: Rollout[]; tasks: Map<string, TaskInfo>; selectedJobId?: string; onSelect: (jobId: string) => void
}) {
  const [filter, setFilter] = useState<JobStage | 'all'>('all')
  const multiModel = new Set(jobs.map(job => job.model).filter(Boolean)).size > 1
  const counts = new Map<string, number>()
  for (const job of jobs) counts.set(job.task_id, (counts.get(job.task_id) ?? 0) + 1)
  const visible = jobs.filter(job => filter === 'all' || jobStage(job) === filter)
    .sort((a, b) => PRIORITY[jobStage(a)] - PRIORITY[jobStage(b)])
  return <section className="experiment-jobs" aria-labelledby="experiment-jobs-title">
    <div className="experiment-section-title"><h2 id="experiment-jobs-title">Jobs <span>{jobs.length}</span></h2><span>Open a job to inspect its rollout</span></div>
    <div className="experiment-job-filters" role="group" aria-label="Job status">
      <button aria-pressed={filter === 'all'} onClick={() => setFilter('all')}>All <span>{jobs.length}</span></button>
      {STAGES.map(({ stage, label }) => <button key={stage} aria-pressed={filter === stage} onClick={() => setFilter(stage)}>
        {label}<span>{jobs.filter(job => jobStage(job) === stage).length}</span>
      </button>)}
    </div>
    <div className="experiment-job-list">
      <div className="experiment-job-head" aria-hidden="true"><span>Task / rollout</span><span>Status</span><span>Progress</span><span>Score</span><span /></div>
      {visible.map(job => {
        const grading = gradingIdentity(job.attempt_kind)
        const stage = jobStage(job)
        const failures = failedAttempts(job.attempts)
        const reward = job.attempts.find(attempt => attempt.classification === 'success')?.summary?.reward
        return <button type="button" key={job.job_id} className="experiment-job-row" aria-pressed={job.job_id === selectedJobId} onClick={() => onSelect(job.job_id)}>
          <span className="experiment-job-task"><strong title={job.task_id}><RedTeamTaskIcon task={tasks.get(job.task_id) ?? job} />{tasks.get(job.task_id)?.title ?? job.task_id}</strong>
            <span>{grading ? <span className="evaluation-label"><Icon name={grading.icon} size={16} />{grading.label}</span> : <>Rollout {job.replicate + 1}/{counts.get(job.task_id)} <span>· seed {job.seed}</span></>}{failures.length > 0 && stage !== 'failed' && <span className="warn-text"> · {failures.length} failed {failures.length === 1 ? 'attempt' : 'attempts'}</span>}</span>
            {multiModel && job.model && <ModelLabel model={job.model} />}
          </span>
          <span className={`job-list-status ${stage}`}>{STAGES.find(item => item.stage === stage)?.label}</span>
          <span className="experiment-job-progress"><JobProgress job={job} /></span>
          <span className="experiment-job-score" aria-label={`Score: ${reward == null ? 'not available' : reward.toFixed(2)}`}>{reward == null ? '—' : reward.toFixed(2)}</span>
          <span className="experiment-job-open"><Icon name="arrow" size={16} /></span>
        </button>
      })}
      {!visible.length && <p className="empty">{jobs.length ? `No ${filter} jobs.` : 'Jobs will appear once the experiment is prepared.'}</p>}
    </div>
  </section>
}
